import React, {useState, useCallback, useEffect} from 'react'
import {Dictionary, flatten, fromPairs, uniq, without} from 'lodash'
import {useDebounce} from 'use-debounce'
import {RouteComponentProps} from '@reach/router'
import {useQuery} from '@apollo/client'
import qs from 'query-string'

import {Page, Container, Loader} from '../layout'
import {searchProductsQuery, getCategoriesQuery} from './queries'
import {GetCategories} from './_gql-types/GetCategories'
import {SearchProducts, SearchProductsVariables} from './_gql-types/SearchProducts'
import {StockAvailability} from '../../_gql-types/global'
import {ChevronDown, ChevronUp, Search} from 'react-bootstrap-icons'
import ProductThumbnail from './ProductThumbnail'
import CategoryFilterItem from './CategoryFilterItem'
import cn from 'classnames'

interface ProductSearchProps {
  filters: SearchProductsVariables
}

function ProductSearch({filters}: ProductSearchProps) {
  const {loading, data} = useQuery<SearchProducts, SearchProductsVariables>(searchProductsQuery, {
    variables: filters,
  })

  const products = data?.products?.edges

  if (loading) {
    return <Loader />
  }

  if (!products?.length) {
    return <>
      Engar vörur fundust fyrir gefin leitarskilyrði
    </>
  }

  return <>
    {products?.map(({node}) => (
      <ProductThumbnail {...node} key={node.id} />
    ))}
  </>
}

type ToggleCategoryFn = (event: React.ChangeEvent<HTMLInputElement>) => void

const getQuery = () => qs.parse(window.location.search, {arrayFormat: 'comma'})

export default function Products(_: RouteComponentProps) {
  const {loading, data} = useQuery<GetCategories>(getCategoriesQuery)
  const categories = data?.categories?.edges.filter(({node}) => node.products?.totalCount)

  const [queryParams, setQueryParams] = useState<any>(getQuery())
  const [showCategories, setShowCategories] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const [debouncedQueryParams] = useDebounce(queryParams, 500)

  const setQueryParam = useCallback((key: string, value?: string | string[]) => {
    setQueryParams({...queryParams, [key]: value})
  }, [queryParams, setQueryParams])

  const queryParamSetter = useCallback((key: string, checkbox: boolean = false) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target
    setQueryParam(key, checkbox
      ? (e.target.checked ? value : undefined)
      : value)
  }, [setQueryParam])

  useEffect(() => {
    const queryString = qs.stringify(queryParams, {
      arrayFormat: 'comma',
      skipNull: true,
      skipEmptyString: true,
    })

    const to = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname

    window.history.replaceState(null, document.title, to)
  }, [queryParams])

  const clearSearch = useCallback(() => {
    setQueryParams({})
  }, [setQueryParams])

  const toggleCategory = useCallback<ToggleCategoryFn>((event) => {
    const query = getQuery()

    const slug = event.target.value

    const currentList = query.flokkar ? flatten([query.flokkar]) : []

    if (event.target.checked) {
      setQueryParam('flokkar', uniq([...currentList, slug]).sort())
    } else {
      setQueryParam('flokkar', without(currentList, slug).sort())
    }
  }, [setQueryParam])

  const selectedCategories: Dictionary<boolean> = queryParams.flokkar
    ? fromPairs(flatten([queryParams.flokkar]).map((slug) => [slug, true]))
    : {}

  const categoryIds = flatten(categories
    ?.filter(({node}) => selectedCategories[node.slug])
    .map(({node}) => {
      const checkedChildrenIds = node.children?.edges
        .filter(({node}) => selectedCategories[node.slug])
        .map(({node}) => node.id)

      const childCount = node.children?.edges?.length ?? 0
      const checkedChildCount = checkedChildrenIds?.length ?? 0

      if (0 < checkedChildCount && checkedChildCount < childCount) {
        return checkedChildrenIds || []
      }

      return [node.id]
    }))

  const filters: SearchProductsVariables = {
    pageSize: 100,
    availability: queryParams.lager === 'ja' ? StockAvailability.IN_STOCK : undefined,
    categories: categoryIds.length ? categoryIds : undefined,
    query: debouncedQueryParams.leit || '',
  }

  return (
    <Page>
      <Container size="lg">
        <div className="row py-5">
          <div className="col-12 col-md-3">
            <div className="sticky-top" style={{ top: 100 }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Vöruleit</h5>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={clearSearch}
                  disabled={!Object.keys(queryParams).length}
                >
                  Hreinsa
                </button>
              </div>

              <div className="mb-4">
                <div className="input-group">
                  <span className="input-group-text border-0 px-3">
                    <Search size={20} />
                  </span>
                  <input
                    className="form-control form-control-lg border-0"
                    type="search"
                    onChange={queryParamSetter("leit")}
                    placeholder="Leita"
                    value={queryParams.leit || ""}
                  />
                </div>
              </div>

              <h5
                className="d-flex justify-content-between mb-4 cursor-pointer"
                onClick={() => setShowFilters(!showFilters)}
              >
                Síur
                <span className="d-md-none">
                  {showFilters ? <ChevronUp /> : <ChevronDown />}
                </span>
              </h5>
              <div
                className={cn("collapse filter-collapse", {
                  show: showFilters,
                })}
              >
                <div className="form-check form-switch mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="filter_field_is_available"
                    onChange={queryParamSetter("lager", true)}
                    value="ja"
                    checked={queryParams.lager === "ja"}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="filter_field_is_available"
                  >
                    Einungis til á lager
                  </label>
                </div>
              </div>

              <h5
                className="mb-4 d-flex justify-content-between cursor-pointer"
                onClick={() => setShowCategories(!showCategories)}
              >
                Flokkar
                <span className="d-md-none">
                  {showCategories ? <ChevronUp /> : <ChevronDown />}
                </span>
              </h5>
              <div
                className={cn("mb-4 collapse filter-collapse", {
                  show: showCategories,
                })}
              >
                {loading ? (
                  <Loader />
                ) : (
                  categories?.map(({ node }) => (
                    <CategoryFilterItem
                      key={node.id}
                      category={node}
                      selected={selectedCategories}
                      toggle={toggleCategory}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-md-9">
            <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 gy-4">
              <ProductSearch filters={filters} />
            </div>
          </div>
        </div>
      </Container>
    </Page>
  )
}
